import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './Header.css';

const Header = ({ 
  currentPage, 
  searchTerm, 
  onSearchChange, 
  onAddNew, 
  showSearch = false,
  showAddButton = false,
  pageTitle,
  titleSuffix
}) => {
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Set browser tab title per section
  useEffect(() => {
    const siteName = 'PEC';
    const titlePart = pageTitle || (function () {
      switch (currentPage) {
        case 'dashboard': return 'Dashboard';
        case 'notes': return 'Notas';
        case 'tasks': return 'Tareas';
        case 'updates': return 'Updates';
        case 'email': return 'Email';
        case 'chatbot': return 'Chatbot';
        case 'finance': return 'Finanzas';
        case 'portfolio': return 'Portfolio';
        case 'data-ingestion': return 'Ingesta de Datos';
        case 'news': return 'Noticias';
        case 'calendar': return 'Calendario';
        case 'n8n-monitor': return 'Monitor';
        case 'rituals': return 'Rituales';
        case 'journal': return 'Diario';
        case 'health': return 'Health & Fitness';
        case 'settings': return 'Configuración';
        default: return 'Personal Hub';
      }
    })();
    document.title = titlePart ? `${siteName} - ${titlePart}` : siteName;
  }, [pageTitle, currentPage]);

  const getPageTitle = () => {
    if (pageTitle) return pageTitle;
    switch (currentPage) {
      case 'dashboard':
        return 'Dashboard';
      case 'notes':
        return 'Notas';
      case 'settings':
        return 'Configuración';
      default:
        return 'Personal Hub';
    }
  };

  return (
    <header className="app-header">
      <div className="header-content">
        {/* Left side - Logo and Page Title */}
        <div className="header-left">
          <div className="logo-section">
            <div className="logo-icon">📋</div>
            <h1 className="page-title">{getPageTitle()}</h1>
            {titleSuffix && (
              <div className="title-suffix" style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {titleSuffix}
              </div>
            )}
          </div>
        </div>

        {/* Center - Search (if enabled) */}
        {showSearch && (
          <div className="header-center">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 21L16.514 16.506M19 10.5A8.5 8.5 0 1110.5 2a8.5 8.5 0 018.5 8.5z" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="search"
                  placeholder="Buscar notas, tags..."
                  value={searchTerm || ''}
                  onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                  className="search-input"
                />
              </div>
              {showAddButton && (
                <button 
                  onClick={onAddNew}
                  className="add-button"
                  title="Nueva Nota"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Right side - Controls and User */}
        <div className="header-right">
          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme}
            className="theme-toggle"
            title={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>

          {/* User Menu */}
          <div className="user-menu-container">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="user-menu-trigger"
            >
              <div className="user-avatar">
                {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <svg className="chevron-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {showUserMenu && (
              <div className="user-menu-dropdown">
                <div className="user-info">
                  <div className="user-email">{currentUser?.email}</div>
                </div>
                <div className="menu-divider"></div>
                <button onClick={() => window.location.href = '/settings'} className="menu-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Configuración
                </button>
                <button onClick={handleLogout} className="menu-item logout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 